import get from "lodash/get";
import { useCallback, useId, useMemo, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AddressRepository } from "@/api/address/repository";
import { AddressService } from "@/api/address/service";
import type {
  IAddress,
  IAddressComponent,
  IAddressSuggestion,
} from "@/types/address";
import type { IFormFieldProps, ISelectOption } from "@/ui-kit";
import { ComboBox, FormField } from "@/ui-kit";
import { AddressUtils } from "@/utils/address";
import { InputControl } from "../InputControl";

type IProps = Partial<IFormFieldProps> & {
  name?: string;
  formElementClass?: string;
};

export const AddressControl = ({
  name = "address",
  label,
  message,
  required,
  formElementClass,
}: IProps) => {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<IAddressSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const { setValue, clearErrors } = useFormContext();
  const address = useWatch({ name });
  const { errors } = useFormState<{ address: IAddress }>();

  const addressRepository = useMemo(() => {
    return new AddressRepository(new AddressService());
  }, []);

  const getAddressComponent = useCallback(
    (components: IAddressComponent[], type: string): string => {
      const component = components.find((comp) => comp.types.includes(type));
      return component?.long_name || "";
    },
    [],
  );

  const getAddressDetails = useCallback(
    (placeId: string, nameValue: string): Promise<void> => {
      return addressRepository
        .getAddressDetails({ placeId })
        .then((response) => {
          const place = response.result;
          const address: IAddress = {
            name: nameValue,
            googlePlaceId: place.place_id,
            addressLine1: place.name,
            addressLine2: "",
            city: getAddressComponent(place.address_components, "locality"),
            state: getAddressComponent(
              place.address_components,
              "administrative_area_level_1",
            ),
            country: getAddressComponent(place.address_components, "country"),
            zipCode: getAddressComponent(
              place.address_components,
              "postal_code",
            ),
            latitude: place.geometry.location.lat,
            longitude: place.geometry.location.lng,
          };
          setValue(name, address, { shouldDirty: true });
          clearErrors(name);
        })
        .catch(() => {
          //
        });
    },
    [addressRepository, getAddressComponent, name, setValue, clearErrors],
  );

  const handleAddressSearch = useCallback(
    (query: string) => {
      if (!query) {
        setSuggestions([]);
        return;
      }

      setIsLoadingSuggestions(true);
      addressRepository
        .getAddresses({ query })
        .then((addresses) => {
          setSuggestions(addresses.predictions || []);
        })
        .catch(() => {
          setSuggestions([]);
        })
        .finally(() => {
          setIsLoadingSuggestions(false);
        });
    },
    [addressRepository],
  );

  const handleAddressChange = useCallback(
    (option: ISelectOption | ISelectOption[] | undefined) => {
      const o = option as ISelectOption | undefined;
      if (o) {
        setValue(`${name}.name`, o.text, { shouldDirty: true });
        void getAddressDetails(o.id, o.text);
      }
    },
    [getAddressDetails, name, setValue],
  );

  const handleAddressClear = useCallback(() => {
    setValue(name, AddressUtils.getEmpty(), { shouldDirty: true });
  }, [name, setValue]);

  const autocompleteOptions: ISelectOption[] = useMemo((): ISelectOption[] => {
    return suggestions.map((suggestion: IAddressSuggestion): ISelectOption => {
      return {
        id: suggestion.place_id,
        text: suggestion.description,
      };
    });
  }, [suggestions]);

  const rootError = get(errors, name);
  const errorMessage = rootError?.name?.message;

  const addressValue: ISelectOption | undefined = useMemo(() => {
    if (!address?.googlePlaceId) {
      return undefined;
    }

    return {
      id: address.googlePlaceId,
      text: address.name,
    };
  }, [address?.googlePlaceId, address?.name]);

  // The FormField label names the ComboBox input through `<label for>`.
  const inputId = useId();

  return (
    <>
      <FormField
        htmlFor={inputId}
        label={label || t("label.address")}
        message={message ?? errorMessage}
        required={required}
        className={formElementClass}
      >
        <ComboBox
          inputId={inputId}
          options={autocompleteOptions}
          value={addressValue}
          onChange={handleAddressChange}
          onSearch={handleAddressSearch}
          onClear={handleAddressClear}
          placeholder={t("placeholder.searchAddress")}
          loading={isLoadingSuggestions}
          invalid={!!errorMessage}
          openLength={3}
        />
      </FormField>

      {address?.googlePlaceId && (
        <>
          <InputControl
            label={t("label.address_line_1")}
            name={`${name}.addressLine1`}
          />
          <InputControl
            label={t("label.address_line_2")}
            name={`${name}.addressLine2`}
          />
          <InputControl label={t("label.city")} name={`${name}.city`} />
          <InputControl label={t("label.state")} name={`${name}.state`} />
          <InputControl label={t("label.country")} name={`${name}.country`} />
          <InputControl
            label={t("label.postal_code")}
            name={`${name}.zipCode`}
          />
        </>
      )}
    </>
  );
};
